<template>
  <div class="oc-page">
    <ApolloQuery
      :query="(gql) => getOrderQuery"
      :variables="{ orderId }"
      :skip="!orderId"
      :update="(data) => data.order"
    >
      <template #default="{ result: { data, error }, isLoading }">
        <div
          v-if="error"
          class="oc-wrapper"
        >
          <apollo-error-alert />
        </div>

        <div
          v-else-if="isLoading"
          class="oc-wrapper"
        >
          <v-skeleton-loader type="article" />
        </div>

        <div
          v-else-if="data"
          class="oc-wrapper"
        >
          <!-- Success hero -->
          <div class="oc-hero">
            <div class="oc-check-circle">
              <ekitabu-icon
                size="40"
                color="white"
              >
                mdi-check
              </ekitabu-icon>
            </div>
            <h1 class="oc-title">
              {{ $t("checkout.complete") }}
            </h1>
            <p class="oc-meta">
              {{ $t('checkout.orderNo', { orderId: data.orderNumber || data._id }) }}
              <span
                v-if="data.updatedAt"
                class="oc-meta-sep"
              >&middot;</span>
              <span v-if="data.updatedAt">{{ formatDate(data.updatedAt) }}</span>
            </p>
          </div>

          <!-- Order items -->
          <EkitabuExpansionPanelDeprecated
            class="oc-panel"
            accordion
            :value="$vuetify.breakpoint.smAndDown ? null : 0"
            :title="$t('checkout.orderNo', { orderId: data.orderNumber || data._id })"
          >
            <div class="oc-items-list">
              <OrderCompleteItem
                v-for="orderLine in data.orderLines.edges.map((edge) => edge.node)"
                :key="orderLine.id"
                :order-line="orderLine"
                :order-currency="data.currency"
              />
            </div>

            <div class="oc-divider" />

            <div class="oc-totals-row">
              <span class="oc-totals-label">
                {{ $t("checkout.total") }}
              </span>
              <price
                class="oc-totals-price"
                :book-price="Number(data.totalPriceSet)"
                :base-currency="data.currency._id"
              />
            </div>
          </EkitabuExpansionPanelDeprecated>

          <!-- Action buttons -->
          <div class="oc-actions">
            <template v-if="isWholesaleOrder(data)">
              <PrimaryButton
                block
                :to="{ name: 'orderOverview', params: { orderId: data?._id, customerId: data?.customerId, customerName: data?.customerEmail } }"
              >
                {{ $t("transactionReports.viewReceipt") }}
                <ekitabu-icon
                  size="18"
                  class="ml-3"
                >
                  mdi-arrow-right
                </ekitabu-icon>
              </PrimaryButton>
            </template>
            <template v-if="hasAudiobooks(data)">
              <div>
                <p class="oc-audiobook-hint">
                  {{ $t('audiobooks.clickAway') }}
                </p>
                <PrimaryButton
                  block
                  @click="() => openAudiobookApp()"
                >
                  {{ $t("bookInfo.listenOnEkitabu") }}
                  <ekitabu-icon
                    size="18"
                    class="ml-4 has-glow"
                  >
                    $headphones
                  </ekitabu-icon>
                </PrimaryButton>
              </div>
            </template>
            <template v-else-if="!hasOnlyPhysicalProducts(data)">
              <PrimaryButton
                block
                :to="{ name: 'bookshelf' }"
              >
                {{ $t("bookshelf.viewOnBookshelf") }}
                <ekitabu-icon
                  size="18"
                  class="ml-3"
                >
                  mdi-arrow-right
                </ekitabu-icon>
              </PrimaryButton>
              <button
                class="oc-ghost-btn"
                @click="$router.push({ name: 'home' })"
              >
                {{ $t('shop.browseMoreBooks') }}
              </button>
            </template>
            <template v-else>
              <!-- Guest Buy Now sign-in prompt -->
              <div
                v-if="isBuyNowGuest"
                class="oc-guest-card"
              >
                <div class="oc-guest-icon-bg">
                  <ekitabu-icon
                    size="24"
                    color="primary"
                  >
                    mdi-account-plus
                  </ekitabu-icon>
                </div>
                <div class="oc-guest-content">
                  <h3 class="oc-guest-title">
                    {{ $t('checkout.orderComplete.guestTitle') }}
                  </h3>
                  <p class="oc-guest-body">
                    {{ $t('checkout.orderComplete.guestBody') }}
                  </p>
                  <PrimaryButton
                    block
                    class="oc-guest-btn"
                    @click="login"
                  >
                    {{ $t('menu.login') }}
                  </PrimaryButton>
                </div>
              </div>

              <PrimaryButton
                block
                @click="$router.push({ name: 'home' })"
              >
                {{ $t("common.close") }}
              </PrimaryButton>
            </template>
          </div>

          <!-- Order fulfillment status -->
          <order-status
            v-if="data?.orderFulfillment"
            class="oc-fulfillment"
            :order="data"
          />
        </div>
      </template>
    </ApolloQuery>
  </div>
</template>

<script>
import currencyMixins from "@/mixins/currencyMixins";
import ApolloErrorAlert from "@/components/ApolloErrorAlert";
import gql from "graphql-tag";
import { getProviderDetails } from '@/utils/PaymentProviderHelper';
import { FORMATS } from '@/utils/Const';
import OrderStatus from './OrderStatus';
import PrimaryButton from "@/components/buttons/PrimaryButton.vue";
import { openAudiobookApp } from "@/helpers/audiobookHelper";
import Price from "@/components/Price.vue";
import OrderCompleteItem from "@/components/lists/OrderCompleteItem.vue";
import EkitabuExpansionPanelDeprecated from "@/components/EkitabuExpansionPanelDeprecated.vue";

/**
 * NOTE for developer working on authentication:
 * This page is now accessible without auth for Buy Now guest orders
 * (via authGuardUnlessBuyNow in router). Buy Now orders are print-only,
 * so hasOnlyPhysicalProducts() returns true and the "Close" button is shown.
 *
 * If adding features that depend on user authentication (e.g. account-based
 * actions), check this.$route.query.flow === 'buyNow' to detect guest orders
 * and handle accordingly.
 */
export default {
  metaInfo() {
    return {
      title: this.$t('routes.confirmed'),
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: this.$t('seo.orderConfirmedDescription'),
        },
      ],
    }
  },
  components: {
    OrderCompleteItem,
    ApolloErrorAlert,
    OrderStatus,
    PrimaryButton,
    Price,
    EkitabuExpansionPanelDeprecated,
  },
  mixins: [currencyMixins],
  data() {
    return {
      orderId: this.$route.params.id,
    };
  },
  computed: {
    getOrderQuery() {
      return gql`
        query GetOrder($orderId: ID!) {
          order(id: $orderId) {
            id
            _id
            orderNumber
            totalPriceSet
            shippingFeeSet
            updatedAt
            customerId
            customerEmail
            orderFulfillment {
              id
              fulfilledAt
              addressLine1
              addressLine2
              addressPostalCode
              phoneNumber
              altPhoneNumber
              school {
                id
                name
              }
              studentName
              studentClass
              createdAt
              updatedAt
            }
            safaricomMpesaRequests {
              id
              merchantRequestId
              resultCode
            }
            dpoPayments {
              id
              transactionReference
              resultCode
            }
            cashPayments {
              id
              succeededAt
            }
            wholesalePayments {
              id
              succeededAt
            }
            currency {
              id
              _id
            }
            orderLines(first: 1000) {
              edges {
                node {
                  id
                  productId
                  name
                  preTaxPrice
                  price
                  discount
                  authorFirstName
                  authorLastName
                  format
                  quantity
                  basePrice
                  baseCurrency {
                    id
                    _id
                  }
                }
              }
            }
          }
        }
      `
    },
    formats() {
      return FORMATS;
    },
    maxItemWidth() {
      return "600px";
    },
    isBuyNowGuest() {
      return this.$route.query.flow === 'buyNow' && !this.$auth.isAuthenticated;
    },
  },
  methods: {
    formatDate(date) {
      let publicationDate = new Date(date);
      var options = { year: "numeric", month: "short", day: "numeric" };
      return publicationDate.toLocaleDateString("en-us", options);
    },
    getPaymentMethodName(data) {
      const paymentMethod = getProviderDetails(data);
      return this.$t(`paymentMethods.${paymentMethod?.provider || 'none'}`);
    },
    hasAudiobooks(order) {
      const orderLines = order.orderLines?.edges?.map(({ node }) => node);
      return !!orderLines.find(
        (orderLine) => orderLine.format == FORMATS.audio
      );
    },
    hasOnlyPhysicalProducts(order) {
      const orderLines = order.orderLines?.edges?.map(({ node }) => node);
      if (!orderLines || orderLines.length === 0) return false;
      return orderLines.every((orderLine) => orderLine.format === FORMATS.print);
    },
    openAudiobookApp,
    isWholesaleOrder(order) {
      return !!order?.wholesalePayments?.find((item) => item.succeededAt != null);
    },
    async login() {
      await this.$auth.loginWithRedirect({
        appState: {
          targetUrl: this.$route.fullPath,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.oc-page {
  min-height: 100vh;
  background: white;
  display: flex;
  justify-content: center;
  padding: 48px 16px 80px;
  font-family: $book-font-body;
}

.oc-wrapper {
  width: 100%;
  max-width: 680px;
}

/* ── Hero ── */
.oc-hero {
  text-align: center;
  margin-bottom: 40px;
}

.oc-check-circle {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: $book-green;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  box-shadow: 0 4px 24px rgba($book-green, 0.28);
}

.oc-title {
  font-family: $book-font-display;
  font-style: italic;
  font-weight: 600;
  font-size: 2rem;
  line-height: 1.2;
  color: $book-ink;
  margin: 0 0 8px;
}

.oc-meta {
  font-family: $book-font-body;
  font-size: 0.875rem;
  color: $book-grey;
  margin: 0;
}

.oc-meta-sep {
  margin: 0 6px;
}

/* ── Expansion panel overrides ── */
.oc-panel {
  margin-bottom: 32px;
}

.oc-panel ::v-deep .ekitabu-expansion-panels {
  box-shadow: none !important;
  border: 1px solid $book-grey-lighter;
}

.oc-panel ::v-deep .v-expansion-panel-header {
  background: $book-grey-lightest !important;
  background-image: none !important;
  color: $book-ink !important;
  font-family: $book-font-body;
  font-weight: 700;
  font-size: 0.8125rem;
  border-bottom: 1px solid $book-grey-lighter;
  min-height: 48px;
}

.oc-panel ::v-deep .v-expansion-panel-content__wrap {
  padding: 0;
}

/* ── Items list ── */
.oc-items-list {
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 14px;
}

/* ── Divider ── */
.oc-divider {
  height: 1px;
  width: 100%;
  background: $book-grey-lighter;
}

/* ── Totals row ── */
.oc-totals-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
}

.oc-totals-label {
  font-family: $book-font-body;
  font-size: 0.875rem;
  font-weight: 800;
  color: $book-ink;
}

.oc-totals-price {
  font-family: $book-font-body;
  font-weight: 800;
  font-size: 0.875rem;
  color: $book-ink;
}

/* Override gradient-text on Price inside totals */
.oc-totals-price ::v-deep .gradient-text {
  background: none !important;
  -webkit-background-clip: unset !important;
  -webkit-text-fill-color: $book-ink !important;
  color: $book-ink !important;
  font-weight: 800;
}

/* ── Action buttons ── */
.oc-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;
}

.oc-audiobook-hint {
  font-family: $book-font-body;
  font-weight: 700;
  font-size: 0.875rem;
  color: $book-ink;
  margin-bottom: 8px;
}

/* Primary button override – apply brand gradient */
.oc-actions ::v-deep .v-btn.primary-button {
  background-image: $book-brand-gradient !important;
  background-color: transparent !important;
  box-shadow: $book-brand-shadow;
  border-radius: 10px !important;
  font-family: $book-font-body;
  font-weight: 700;
  font-size: 0.875rem;
  letter-spacing: 0.01em;
  text-transform: none;
  height: 48px !important;
  transition: transform 0.3s $book-ease-out, box-shadow 0.3s $book-ease-out;
}

.oc-actions ::v-deep .v-btn.primary-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 24px rgba($book-red-dark, 0.38);
}

/* Ghost / secondary button */
.oc-ghost-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 48px;
  border: 1.5px solid $book-grey-light;
  border-radius: 10px;
  background: transparent;
  color: $book-ink;
  font-family: $book-font-body;
  font-weight: 700;
  font-size: 0.875rem;
  cursor: pointer;
  transition: border-color 0.25s $book-ease-out, background 0.25s $book-ease-out;
}

.oc-ghost-btn:hover {
  border-color: $book-ink;
  background: $book-grey-lightest;
}

/* ── Guest card ── */
.oc-guest-card {
  display: flex;
  gap: 16px;
  background: $book-grey-lightest;
  border: 1px solid $book-grey-lighter;
  border-radius: 12px;
  padding: 24px;
  text-align: left;
  margin-bottom: 8px;
}

.oc-guest-icon-bg {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.oc-guest-content {
  flex: 1;
}

.oc-guest-title {
  font-family: $book-font-body;
  font-weight: 800;
  font-size: 1rem;
  color: $book-ink;
  margin: 0 0 4px;
}

.oc-guest-body {
  font-family: $book-font-body;
  font-size: 0.875rem;
  color: $book-grey;
  margin: 0 0 16px;
  line-height: 1.5;
}

.oc-guest-btn {
  height: 40px !important;
}

/* ── Fulfillment card ── */
.oc-fulfillment {
  border: 1px solid $book-grey-lighter;
  border-radius: 12px;
  overflow: hidden;
}

.oc-fulfillment ::v-deep .order-status-container {
  border: none;
  background: $book-grey-lightest;
  font-family: $book-font-body;
}

/* ── Responsive ── */
@media (max-width: 899px) {
  .oc-page {
    padding: 32px 12px 64px;
  }

  .oc-title {
    font-size: 1.625rem;
  }

  .oc-check-circle {
    width: 60px;
    height: 60px;
    margin-bottom: 16px;
  }

}
</style>
